import { getAvailableIssueTypes, getRequests } from "actions/requests";
import { ACTIONS } from "actions/requests/types";
import { UserRequest } from "api-client/models/UserRequest";
import LoadingSpinner from "components/Loaders/LoadingSpinner";
import useSelector from "hooks/useSelector";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Option } from "types";
import { LoadingState } from "types/api";
import { LoadingSpinnerSize } from "types/loadingSpinner";
import { Request } from "types/requests";
import styles from "./CaseManagement.module.scss";
import { MyRequests } from "./MyRequests";
import { NoRequests } from "./NoRequests";
import { RequestForm } from "./RequestForm";
import CaseManagementHeader from "./CaseManagementHeader";
import Modal from "components/Modals/Modal";
import CloseButton from "components/Buttons/CloseButton";
import useBreakPoint from "hooks/useBreakPoint";
import { Breakpoint } from "types/breakpoint";
import CollapseButton from "components/Buttons/CollapseButton";

const ConnectedCaseManagement = () => {
    const dispatch = useDispatch();
    const availableIssueTypes = useSelector((state) => state.requests.availableIssueTypes);
    const userRequests = useSelector((state) => state.requests.userRequests);
    const apiBaseUrl = useSelector((state) => state.appConfiguration.apiBaseUrl);
    const [requestPage, setRequestPage] = useState<number>(1);

    useEffect(() => {
        availableIssueTypes.status === LoadingState.idle && dispatch(getAvailableIssueTypes());
        dispatch(getRequests(requestPage));
    }, [requestPage]);

    const loadMoreRequests = (page: number) => {
        setRequestPage(page + 1);
    }

    const handleRequestCreated = (request: UserRequest) =>
        dispatch({
            type: ACTIONS.CREATE_REQUEST_SUCCESS,
            payload: request,
        });

    return (
        <CaseManagement
            availableIssueTypes={availableIssueTypes.data || undefined}
            userRequests={userRequests.data || undefined}
            apiBaseUrl={apiBaseUrl}
            onRequestCreated={handleRequestCreated}
            isNextPage={userRequests.isNextPage}
            onLoadMoreRequests={() => loadMoreRequests(requestPage)}
            isLoadingMore={userRequests.status === LoadingState.loading}
        />
    );
};

interface CaseManagementProps {
    availableIssueTypes?: Option[];
    userRequests?: Request[];
    apiBaseUrl: string;
    onRequestCreated(request: UserRequest): void;
    onLoadMoreRequests: () => void;
    isNextPage: boolean;
    isLoadingMore: boolean;
}

interface SupportRequestModalProps {
    onClose(): void;
    selectedTab: string;
    tabs: {
        requestForm: string;
        myRequests: string;
    };
    requestForm: React.ReactNode;
    showRequestForm: boolean;
    requestComponent: React.ReactNode;
}
const SupportRequestModal: React.FC<SupportRequestModalProps> = ({
    onClose,
    selectedTab,
    tabs,
    requestForm,
    showRequestForm,
    requestComponent,
}) => {
    const { formatMessage } = useIntl();
    return (
        <Modal
            classes={{ container: styles["modal-container"] }}
            isVisible
            onClickBackdrop={onClose}
        >
            <div className={styles["modal-header"]}>
                <CloseButton className="modal-close" handleClose={onClose} />
                <div className={styles["selectedtab-header"]}>
                    {selectedTab === tabs.myRequests
                        ? formatMessage({ id: "case_management.tab.my_requests_text_selected" })
                        : tabs.requestForm}
                </div>
                {showRequestForm ? requestForm : requestComponent}
            </div>
        </Modal>
    );
};

export const CaseManagement: React.FC<CaseManagementProps> = ({
    availableIssueTypes,
    userRequests,
    apiBaseUrl,
    onRequestCreated,
    isNextPage,
    onLoadMoreRequests,
    isLoadingMore
}) => {
    const isSmallMobile = useBreakPoint(Breakpoint.smallMobile);
    const { formatMessage } = useIntl();

    const tabs = {
        requestForm: formatMessage({ id: "case_management.tab.request_form_text" }),
        myRequests: formatMessage({ id: "case_management.tab.my_requests_text_button" }),
    };
    const [selectedTab, setSelectedTab] = useState(tabs.requestForm);

    const [isOpenSupportRequestModal, setIsOpenRequestModal] = useState(true);

    const showRequestForm = selectedTab === tabs.requestForm;
    const requestComponent =
        userRequests && userRequests.length > 0 ? (
            <MyRequests userRequests={userRequests} isNextPage={isNextPage} onLoadMoreRequests={onLoadMoreRequests} isLoadingMore={isLoadingMore} />
        ) : (
            <NoRequests isLoading={!userRequests} />
        );

    const requestForm = availableIssueTypes ? (
        <RequestForm
            availableIssueTypes={availableIssueTypes}
            apiBaseUrl={apiBaseUrl}
            onRequestCreated={onRequestCreated}
        />
    ) : (
        <LoadingSpinner size={LoadingSpinnerSize.medium} />
    );
    const minimizeAction = () => {
        setIsOpenRequestModal(!isOpenSupportRequestModal);
    };

    const selectView = showRequestForm ? requestForm : requestComponent;
    return (
        <>
            <div
                className={
                    isOpenSupportRequestModal
                        ? styles["case-management-minimized"]
                        : styles["case-management"]
                }
            >
                <CaseManagementHeader
                    setSelected={setSelectedTab}
                    selectedtab={selectedTab}
                    tab={tabs}
                    isOpenRequests={isOpenSupportRequestModal}
                    setIsOpenRequests={setIsOpenRequestModal}
                />
                <div className={isOpenSupportRequestModal ? styles["minimize"] : ""}>
                    {isSmallMobile ? (
                        <SupportRequestModal
                            requestForm={requestForm}
                            showRequestForm={showRequestForm}
                            requestComponent={requestComponent}
                            selectedTab={selectedTab}
                            tabs={tabs}
                            onClose={minimizeAction}
                        />
                    ) : (
                        selectView
                    )}
                </div>
                <div className={styles["collapse-container"]}>
                    {!isOpenSupportRequestModal && (
                        <CollapseButton onClick={minimizeAction} />
                    )}
                </div>
            </div>
        </>
    );
};

export default ConnectedCaseManagement;
